import { ROOT_FOLDER_PATH } from '@/common/constants';
import { DataRoomClientFile } from '@/common/types';
import { Folder } from '@/contexts/overview/dataroom/DataRoomContext';
import { compareCardinalPaths } from '@/pages/overview/common/utils';

// Type for valid folder paths
export type FolderPath = `/${string}`;

// Type guard to check if a string is a valid folder path
export const isValidFolderPath = (path: string): path is FolderPath => {
  return path.startsWith('/');
};

/**
 * Shared file sorting function to ensure consistent ordering across the application
 * Sorts by displayName (or name if no displayName), then by name as tiebreaker
 */
export const sortDataRoomFiles = (a: DataRoomClientFile, b: DataRoomClientFile): number => {
  const nameCompare = compareCardinalPaths(a.displayName || a.name, b.displayName || b.name);
  if (nameCompare !== 0) return nameCompare;
  return a.name.localeCompare(b.name);
};

// Get all paths where a file exists in the folder structure
export const getFileAllPaths = (
  file: DataRoomClientFile,
  foldersByPaths: Map<string, Folder[]>,
): FolderPath[] => {
  const filePaths: FolderPath[] = [];

  // Find all folders that contain this file
  for (const [folderPath, folders] of foldersByPaths.entries()) {
    const folder = folders[0]; // We know there's only one folder per path
    if (folder.files?.some((f) => f.id === file.id)) {
      filePaths.push(`${folderPath}/${file.displayName || file.name}` as FolderPath);
    }
  }

  // If file is not in any folder, it's in root
  if (filePaths.length === 0) {
    filePaths.push(`${ROOT_FOLDER_PATH}/${file.name}` as FolderPath);
  }

  return filePaths;
};

// TODO we should remove this function. you should either want all paths or know the context exactly
// Get the primary path for a file (for display purposes)
export const getFilePrimaryPath = (
  file: DataRoomClientFile,
  foldersByPaths: Map<string, Folder[]>,
): FolderPath => {
  const allPaths = getFileAllPaths(file, foldersByPaths);

  // Primary path selection logic:
  // 1. Prefer non-trash paths
  // 2. Prefer shorter paths (closer to root)
  // 3. Prefer alphabetically first path as tiebreaker
  const nonTrashPaths = allPaths.filter((path) => !isTrashPath(path));
  const pathsToConsider = nonTrashPaths.length > 0 ? nonTrashPaths : allPaths;

  return pathsToConsider.sort((a, b) => {
    const aDepth = getFolderDepth(a);
    const bDepth = getFolderDepth(b);
    if (aDepth !== bDepth) return aDepth - bDepth;
    return a.localeCompare(b);
  })[0];
};

// Get the path of a file in a specific folder context
export const getFilePathInContext = (
  file: DataRoomClientFile,
  foldersByPaths: Map<string, Folder[]>,
  contextPath?: string,
): FolderPath => {
  const allPaths = getFileAllPaths(file, foldersByPaths);

  // If no context specified, return primary path
  if (!contextPath) {
    return getFilePrimaryPath(file, foldersByPaths);
  }

  // Try to find a path that matches the context
  const contextFile = `${contextPath}/${file.displayName || file.name}` as FolderPath;
  if (allPaths.includes(contextFile)) {
    return contextFile;
  }

  // If context doesn't match, return primary path
  return getFilePrimaryPath(file, foldersByPaths);
};

// Legacy function for backward compatibility
// TODO: Replace all usages with getFilePrimaryPath or getFilePathInContext
export const getFileCurrentPath = (
  file: DataRoomClientFile,
  foldersByPaths: Map<string, Folder[]>,
): FolderPath => {
  return getFilePrimaryPath(file, foldersByPaths);
};

// Get the parent path of a given path
export const getParentPath = (path: FolderPath): FolderPath | null => {
  const parts = path.split('/').filter(Boolean);
  if (parts.length <= 1) return null;
  return `/${parts.slice(0, -1).join('/')}` as FolderPath;
};

// Get the folder name from a path
export const getFolderNameFromPath = (path: FolderPath): string => {
  return path.split('/').pop() || '';
};

// Check if a path is a subpath of another path
export const isSubPath = (parentPath: FolderPath, childPath: FolderPath): boolean => {
  if (parentPath === childPath) return false;
  return childPath.startsWith(parentPath + '/');
};

// Get all subpaths of a given path
export const getSubPaths = (path: FolderPath, allPaths: FolderPath[]): FolderPath[] => {
  return allPaths.filter((p) => isSubPath(path, p));
};

// Get the depth of a folder path
export const getFolderDepth = (path: FolderPath): number => {
  return path.split('/').filter(Boolean).length - 1; // -1 because root doesn't count
};

// Get the display name of a folder from its path
export const getFolderDisplayName = (
  path: FolderPath,
  foldersByPaths: Map<string, Folder[]>,
): string => {
  const folder = foldersByPaths.get(path)?.[0];
  return folder?.displayName || folder?.name || getFolderNameFromPath(path);
};

// Check if a path is a root path
export const isRootPath = (path: FolderPath): boolean => {
  return path === ROOT_FOLDER_PATH;
};

// Check if a path is a trash path
export const isTrashPath = (path: FolderPath): boolean => {
  return path === `${ROOT_FOLDER_PATH}/~trash`;
};

// Get all files in a folder and its subfolders
export const getFilesInFolderAndSubfolders = (
  path: FolderPath,
  foldersByPaths: Map<string, Folder[]>,
  pathOrganizedDocuments: Map<string, DataRoomClientFile[]>,
): DataRoomClientFile[] => {
  const subPaths = getSubPaths(path, Array.from(foldersByPaths.keys()) as FolderPath[]);
  const allPaths = [path, ...subPaths];

  return allPaths.flatMap((p) => pathOrganizedDocuments.get(p) || []);
};

// Sort paths in a hierarchical order
export const sortPathsHierarchically = (paths: FolderPath[]): FolderPath[] => {
  return [...paths].sort((a, b) => {
    // Sort by path length first to ensure parents come before children
    const aLength = a.split('/').length;
    const bLength = b.split('/').length;
    if (aLength !== bLength) return aLength - bLength;
    // Then sort alphabetically
    return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
  });
};

// Check if a file exists in multiple folders
export const isLinkedFile = (
  file: DataRoomClientFile,
  foldersByPaths: Map<string, Folder[]>,
): boolean => {
  return getFileAllPaths(file, foldersByPaths).length > 1;
};

// Check if a file should be selectable (has at least one non-trash location)
export const isFileSelectable = (
  file: DataRoomClientFile,
  foldersByPaths: Map<string, Folder[]>,
): boolean => {
  const allPaths = getFileAllPaths(file, foldersByPaths);
  // A file is selectable if it has at least one non-trash location
  return allPaths.some((filePath) => {
    const lastSlashIndex = filePath.lastIndexOf('/');
    const folderPath = filePath.substring(0, lastSlashIndex) as FolderPath;
    return !isTrashPath(folderPath);
  });
};

// Get all folder paths where a file exists (without file name)
export const getFileFolderPaths = (
  file: DataRoomClientFile,
  foldersByPaths: Map<string, Folder[]>,
): FolderPath[] => {
  const folderPaths: FolderPath[] = [];

  for (const [folderPath, folders] of foldersByPaths.entries()) {
    const folder = folders[0];
    if (folder.files?.some((f) => f.id === file.id)) {
      folderPaths.push(folderPath as FolderPath);
    }
  }

  return folderPaths;
};

// Check if a file exists in a specific folder path
export const isFileInFolder = (
  file: DataRoomClientFile,
  folderPath: string,
  foldersByPaths: Map<string, Folder[]>,
): boolean => {
  const folders = foldersByPaths.get(folderPath);
  if (!folders || folders.length === 0) return false;

  const folder = folders[0];
  return folder.files?.some((f) => f.id === file.id) || false;
};

// Determine the best source folder for a file (prioritizes non-trash folders)
export const determineSourceFolderForFile = (
  file: DataRoomClientFile,
  foldersByPaths: Map<string, Folder[]>,
): string | undefined => {
  const filePaths = getFileAllPaths(file, foldersByPaths);
  const nonTrashPaths = filePaths.filter((path) => {
    const folderPath = path.substring(0, path.lastIndexOf('/'));
    return !isTrashPath(folderPath as FolderPath);
  });

  const sourcePath = nonTrashPaths.length > 0 ? nonTrashPaths[0] : filePaths[0];
  if (!sourcePath) return undefined;

  const sourceFolderPath = sourcePath.substring(0, sourcePath.lastIndexOf('/'));
  return foldersByPaths.get(sourceFolderPath)?.[0]?.key;
};

// Build fileMoves array for the new API format
export const buildFileMoves = (
  files: DataRoomClientFile[],
  foldersByPaths: Map<string, Folder[]>,
  sourceContext?: Map<string, string>, // fileId -> folderPath
): Array<{ fileName: string; sourceFolderKey?: string }> => {
  return files.map((file) => {
    let sourceFolderKey: string | undefined;

    // Try source context first (for drag operations)
    if (sourceContext?.has(file.id)) {
      const folderPath = sourceContext.get(file.id)!;
      sourceFolderKey = foldersByPaths.get(folderPath)?.[0]?.key;
    } else {
      // Fallback to determining primary source folder
      sourceFolderKey = determineSourceFolderForFile(file, foldersByPaths);
    }

    return {
      fileName: file.name,
      sourceFolderKey,
    };
  });
};
